import { useGridApiRef } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';

type GridApiRef = ReturnType<typeof useGridApiRef>;

const EDGE_TOLERANCE_PX = 2;

// Steps the grid's horizontal scroll one column at a time, aligning a column's left edge with the viewport's.
export const useColumnStepScroll = (apiRef: GridApiRef) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const update = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;

    const viewportWidth = api.getRootDimensions()?.viewportInnerSize.width ?? 0;
    const totalWidth = api.getVisibleColumns().reduce((sum, column) => sum + column.computedWidth, 0);
    const { left } = api.getScrollPosition();
    const overflows = viewportWidth > 0 && totalWidth - viewportWidth > EDGE_TOLERANCE_PX;

    setCanScrollLeft(overflows && left > EDGE_TOLERANCE_PX);
    setCanScrollRight(overflows && left + viewportWidth < totalWidth - EDGE_TOLERANCE_PX);
  }, [apiRef]);

  useEffect(() => {
    const api = apiRef.current;
    if (!api) return undefined;

    const unsubscribes = [
      api.subscribeEvent('scrollPositionChange', update),
      api.subscribeEvent('columnsChange', update),
      api.subscribeEvent('viewportInnerSizeChange', update),
      api.subscribeEvent('virtualScrollerContentSizeChange', update),
    ];
    window.addEventListener('resize', update);
    update();

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
      window.removeEventListener('resize', update);
    };
  }, [apiRef, update]);

  const columnLefts = useCallback(() => {
    const api = apiRef.current;
    if (!api) return [];
    return api.getVisibleColumns().map((column) => api.getColumnPosition(column.field));
  }, [apiRef]);

  const scrollRight = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    const { left } = api.getScrollPosition();
    const next = columnLefts().find((position) => position > left + EDGE_TOLERANCE_PX);
    if (next !== undefined) api.scroll({ left: next });
  }, [apiRef, columnLefts]);

  const scrollLeft = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    const { left } = api.getScrollPosition();
    const previous = columnLefts()
      .filter((position) => position < left - EDGE_TOLERANCE_PX)
      .pop();
    if (previous !== undefined) api.scroll({ left: previous });
  }, [apiRef, columnLefts]);

  return { canScrollLeft, canScrollRight, scrollLeft, scrollRight };
};
